import React, { useEffect, useRef, useState } from 'react';
import { serviceBundler } from '../../App';
import { Badge, FloatButton, Layout, Space, theme, Tour } from 'antd';
import { Content, Footer, Header } from 'antd/es/layout/layout';
import {
  BellOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import type { Button, GetRef, TourProps } from 'antd';

import style from './DevicesPage.module.css';
import { useNavigate } from 'react-router-dom';
import HeaderBar from '../../Components/Header/HeaderBar';
import { useTranslation } from 'react-i18next';
import { AddDevice } from '../../Components/Devices/Forms/AddDevice/AddDevice';
import { ResultAddDevice } from '../../Enums/ResultAddDevice';
import CustomLoader from '../../Components/CustomLoader/CustomLoader';
import NotificationDeviceList from '../../Components/Devices/Lists/NotificationDeviceList/NotificationDeviceList';
import { ShowTour } from '../../Components/Devices/Forms/Tour/ShowTour';
import FooterBar from '../../Components/Footer/FooterBar';
import { TourHelper } from '../../Helpers/TourHelper';

const { useToken } = theme;

const DevicesPage = () => {
  const { t, i18n } = useTranslation();
  const { token } = useToken();

  let navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [editDevice, setEditDevice] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [countNotification, setCountNotification] = useState(0);

  const notificationDevicesRef = useRef<any>(null);
  const counterDevicesRef = useRef<any>(null);
  const controlDevicesRef = useRef<any>(null);

  const [currentTourStep, setCurrentTourStep] = useState<number>(0);

  const refStep1 = useRef<any>(null);
  const ref2 = useRef<GetRef<typeof Button>>(null);
  const ref3 = useRef<GetRef<typeof Button>>(null);

  useEffect(() => {
    if (loading) {
      HandleIcon();
    }
  }, [loading]);

  useEffect(() => {
    if (serviceBundler.tourService.tourDevicesPageActive) {
      if (!loading) {
        const timeout = setTimeout(() => {
          SetNumberOfTourStep(1);
        }, 150);
      }
    }
  }, [loading]);

  useEffect(() => {
    serviceBundler.tourService.TourDevicesPageSubject.subscribe((value) => {
      if (value) {
        if (serviceBundler.tourService.tourDevicesPageActive) {
          const timeout = setTimeout(() => {
            SetNumberOfTourStep(1);
          }, 150);
        }
      }
    });
  }, [0]);

  const steps: TourProps['steps'] = [
    TourHelper.CreateTourSteps(
      t,
      'Alle apparaten',
      'Hier zie je alle apparaten die verbonden zijn met de applicatie. \n Je kunt apparaten toevoegen, verwijderen en beheren. \n Klik op een apparaat om meer details te zien.',
      'top',
      refStep1.current,
    ),
  ];

  const setEditModeCallBack = (value: boolean) => {
    setCurrentTourStep(0);
    setEditDevice(value);

    const nodeNo = notificationDevicesRef.current;
    if (nodeNo) {
      nodeNo.SetExternalEdit(value);
    }

    const nodeCou = counterDevicesRef.current;
    if (nodeCou) {
      nodeCou.SetExternalEdit(value);
    }

    const nodeCon = controlDevicesRef.current;
    if (nodeCon) {
      nodeCon.SetExternalEdit(value);
    }
  };

  const HandleIcon = () => {
    setLoading(false);

    serviceBundler.notificationService.notificationsChannel.subscribe(
      (value) => {
        if (Array.isArray(value)) {
          setCountNotification(
            serviceBundler.notificationService.notificationsProcessed.length,
          );
        }
      },
    );

    serviceBundler.notificationService.notificationsToKillChannel.subscribe(
      (value) => {
        if (Array.isArray(value)) {
          setCountNotification(
            serviceBundler.notificationService.notificationsProcessed.length,
          );
        }
      },
    );
  };

  const ChooseDevice = (result: ResultAddDevice) => {
    if (result == ResultAddDevice.AddNotificationDevice) {
      const node = notificationDevicesRef.current;
      if (node) {
        node.AddDevice();
      }
    }

    setEditDevice(true);
    setIsModalOpen(false);
  };

  const NavigateToHome = () => {
    navigate('/');
  };

  const OpenModalForAdd = () => {
    setIsModalOpen(true);
  };

  const StartSpecicifcTour = (step: number) => {
    if (currentTourStep == step) {
      return true;
    }
    return false;
  };

  const SetNumberOfTourStep = (index: number) => {
    if (!editDevice || !isModalOpen) setCurrentTourStep(index);
  };

  const TourReady = () => {
    serviceBundler.tourService.EndDevicesPageTour();
    setCurrentTourStep(0);
  };

  return (
    <Space direction="vertical" style={{ width: '100%', height: '100vh' }}>
      <Layout>
        <Tour
          open={StartSpecicifcTour(1)}
          onClose={() => TourReady()}
          steps={steps}
        />
        <Header className={style.headerStyle}>
          <HeaderBar></HeaderBar>
        </Header>

        <Content
          style={{ backgroundColor: token?.colorPrimaryBg }}
          className={style.headerBlock}
        >
          {!editDevice && (
            <>
              <div className={style.bodyTextBadge}>
                <div
                  style={{ color: token?.colorPrimaryText }}
                  className={style.textBody}
                >
                  {t('connectedDevices')}:
                </div>
                <Badge
                  count={countNotification}
                  showZero
                  color="rgb(193, 210, 2)"
                >
                  <BellOutlined
                    onClick={NavigateToHome}
                    className={style.badgeHover}
                    style={{ fontSize: 25 }}
                  />
                </Badge>
              </div>
            </>
          )}

          {loading && (
            <div
              style={{
                marginTop: 150,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                verticalAlign: 'middle',
              }}
            >
              <CustomLoader></CustomLoader>
            </div>
          )}

          {!loading && (
            <>
              <div ref={refStep1} className={style.containerDevices}>
                <NotificationDeviceList
                  ref={notificationDevicesRef}
                  isLoading={loading}
                  isEdit={setEditModeCallBack}
                ></NotificationDeviceList>
              </div>
              <AddDevice
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onChoose={ChooseDevice}
              ></AddDevice>

              <FloatButton
                onClick={OpenModalForAdd}
                shape="square"
                tooltip={t('addDevice')}
                icon={<PlusCircleOutlined />}
                type="default"
                style={{ bottom: 100, right: 30 }}
              />
            </>
          )}
        </Content>
        <Footer className={style.footerStyle}>
          <FooterBar></FooterBar>
        </Footer>
      </Layout>
    </Space>
  );
};

export default DevicesPage;
